import offlinePayloadCrypto from './offlineCrypto';
import offlineDb, {
  type OfflineDomainRecord,
  type OfflineMetaRecord,
  type OfflineOutboxRecord,
  type OfflineOutboxStatus,
  type OfflineRecordSyncStatus,
} from './offlineDb';

const DOMAIN = 'workitems' as const;

export type WorkItemsStoreName =
  | 'work_items'
  | 'photos'
  | 'signatures'
  | 'checklists'
  | 'time_entries'
  | 'documents';

export type WorkItemsMetaKey = 'sync_metadata';
export type WorkItemsOutboxStatus = OfflineOutboxStatus;

export interface WorkItemsOutboxEntry<TPayload = unknown> {
  id: number;
  operation: string;
  entity_type: string;
  entity_id: string;
  payload: TPayload;
  priority: number;
  status: WorkItemsOutboxStatus;
  attempts: number;
  max_attempts: number;
  last_attempt?: Date;
  next_retry_at?: Date;
  error_message?: string;
  created_at: Date;
  updated_at: Date;
}

interface UpsertRecordOptions {
  parentId?: string;
  status?: string;
  syncStatus?: OfflineRecordSyncStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface AddOutboxEntryInput<TPayload> {
  operation: string;
  entityType: string;
  entityId: string | number;
  payload: TPayload;
  priority: number;
  status?: WorkItemsOutboxStatus;
  attempts?: number;
  maxAttempts?: number;
  lastAttempt?: Date | string;
  nextRetryAt?: Date | string;
  errorMessage?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface UpdateOutboxEntryInput<TPayload> {
  operation?: string;
  entityType?: string;
  entityId?: string | number;
  payload?: TPayload;
  priority?: number;
  status?: WorkItemsOutboxStatus;
  attempts?: number;
  maxAttempts?: number;
  lastAttempt?: Date | string | null;
  nextRetryAt?: Date | string | null;
  errorMessage?: string | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

function getDb() {
  return offlineDb;
}

function toIso(value?: Date | string | null, fallback?: string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return fallback || new Date().toISOString();
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

function toDate(value?: string | null): Date | undefined {
  return value ? new Date(value) : undefined;
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }

  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T extends { id: string | number }>(
  store: WorkItemsStoreName,
  clientId: number,
  item: T,
  existing: OfflineDomainRecord | undefined,
  options?: UpsertRecordOptions,
): Promise<OfflineDomainRecord> {
  const now = toIso(options?.updatedAt);
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: toEntityId(item.id),
    parent_id: options?.parentId,
    status: options?.status,
    sync_status: options?.syncStatus || existing?.sync_status || 'synced',
    created_at: existing?.created_at || toIso(options?.createdAt, now),
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

function buildMetaStorageKey(clientId: number, key: WorkItemsMetaKey): string {
  return `${DOMAIN}:${clientId}:${key}`;
}

async function getStoreRows(store: WorkItemsStoreName, clientId: number): Promise<OfflineDomainRecord[]> {
  const db = getDb();
  if (!db) {
    return [];
  }

  return db.records
    .where('[domain+store+client_id]')
    .equals([DOMAIN, store, clientId])
    .toArray();
}

async function getStoreRow(store: WorkItemsStoreName, clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }

  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

const workItemsStore = {
  async upsertRecord<T extends { id: string | number }>(
    store: WorkItemsStoreName,
    clientId: number,
    item: T,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const entityId = toEntityId(item.id);
    const existing = await getStoreRow(store, clientId, entityId);
    const row = await buildRecordRow(store, clientId, item, existing, options);
    await db.records.put(row);
  },

  async bulkUpsertRecords<T extends { id: string | number }>(
    store: WorkItemsStoreName,
    clientId: number,
    items: T[],
    getOptions?: (item: T) => UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db || items.length === 0) {
      return;
    }

    const existingRows = await getStoreRows(store, clientId);
    const existingMap = new Map(existingRows.map((row) => [row.entity_id, row]));
    const rows = await Promise.all(
      items.map((item) => {
        const entityId = toEntityId(item.id);
        return buildRecordRow(store, clientId, item, existingMap.get(entityId), getOptions?.(item));
      }),
    );

    await db.records.bulkPut(rows);
  },

  async replaceStoreRecords<T extends { id: string | number }>(
    store: WorkItemsStoreName,
    clientId: number,
    items: T[],
    getOptions?: (item: T) => UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const existingRows = await getStoreRows(store, clientId);
    const existingMap = new Map(existingRows.map((row) => [row.entity_id, row]));
    const incomingEntityIds = new Set(items.map((item) => toEntityId(item.id)));
    const rows = await Promise.all(
      items.map((item) => {
        const entityId = toEntityId(item.id);
        return buildRecordRow(store, clientId, item, existingMap.get(entityId), getOptions?.(item));
      }),
    );

    const idsToDelete = existingRows
      .filter((row) => !incomingEntityIds.has(row.entity_id))
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');

    await db.transaction('rw', db.records, async () => {
      if (idsToDelete.length > 0) {
        await db.records.bulkDelete(idsToDelete);
      }
      if (rows.length > 0) {
        await db.records.bulkPut(rows);
      }
    });
  },

  async getRecord<T>(store: WorkItemsStoreName, clientId: number, entityId: string | number): Promise<T | undefined> {
    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    return decryptRecordPayload<T>(row);
  },

  async listRecords<T>(store: WorkItemsStoreName, clientId: number): Promise<T[]> {
    const rows = await getStoreRows(store, clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((accumulator, value) => {
      if (value !== undefined) {
        accumulator.push(value);
      }
      return accumulator;
    }, []);
  },

  async listRecordsBySyncStatus<T>(
    store: WorkItemsStoreName,
    clientId: number,
    syncStatus: OfflineRecordSyncStatus,
  ): Promise<T[]> {
    const rows = await getStoreRows(store, clientId);
    const matched = rows.filter((row) => row.sync_status === syncStatus);
    const values = await Promise.all(matched.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((accumulator, value) => {
      if (value !== undefined) {
        accumulator.push(value);
      }
      return accumulator;
    }, []);
  },

  async listRecordsByParent<T>(store: WorkItemsStoreName, clientId: number, parentId: string | number): Promise<T[]> {
    const db = getDb();
    if (!db) {
      return [];
    }

    const rows = await db.records
      .where('[domain+store+client_id+parent_id]')
      .equals([DOMAIN, store, clientId, toEntityId(parentId)])
      .toArray();
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((accumulator, value) => {
      if (value !== undefined) {
        accumulator.push(value);
      }
      return accumulator;
    }, []);
  },

  async filterRecords<T>(
    store: WorkItemsStoreName,
    clientId: number,
    predicate: (item: T) => boolean,
  ): Promise<T[]> {
    const items = await this.listRecords<T>(store, clientId);
    return items.filter(predicate);
  },

  async countRecords(
    store: WorkItemsStoreName,
    clientId: number,
    predicate?: (item: unknown) => boolean,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    if (!predicate) {
      return db.records
        .where('[domain+store+client_id]')
        .equals([DOMAIN, store, clientId])
        .count();
    }

    const items = await this.listRecords<unknown>(store, clientId);
    return items.filter(predicate).length;
  },

  async countRecordsByStatus(
    store: WorkItemsStoreName,
    clientId: number,
    status: string,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    return db.records
      .where('[domain+store+client_id+status]')
      .equals([DOMAIN, store, clientId, status])
      .count();
  },

  async getStorePayloadSize(store: WorkItemsStoreName, clientId: number): Promise<number> {
    const rows = await getStoreRows(store, clientId);
    return rows.reduce((total, row) => total + (row.payload_size || 0), 0);
  },

  async deleteRecord(store: WorkItemsStoreName, clientId: number, entityId: string | number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async deleteRecordsWhere<T>(
    store: WorkItemsStoreName,
    clientId: number,
    predicate: (item: T) => boolean,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    const rows = await getStoreRows(store, clientId);
    const idsToDelete: number[] = [];

    for (const row of rows) {
      const item = await decryptRecordPayload<T>(row);
      if (item !== undefined && predicate(item) && typeof row.id === 'number') {
        idsToDelete.push(row.id);
      }
    }

    if (idsToDelete.length > 0) {
      await db.records.bulkDelete(idsToDelete);
    }

    return idsToDelete.length;
  },

  async clearStore(store: WorkItemsStoreName, clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const rows = await getStoreRows(store, clientId);
    const ids = rows
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');

    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async clearClient(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const [recordRows, outboxRows, metaRows] = await Promise.all([
      db.records.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
      db.outbox.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
      db.meta.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray(),
    ]);

    const recordIds = recordRows
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');
    const outboxIds = outboxRows
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');
    const metaKeys = metaRows.map((row) => row.key);

    await db.transaction('rw', db.records, db.outbox, db.meta, async () => {
      if (recordIds.length > 0) {
        await db.records.bulkDelete(recordIds);
      }
      if (outboxIds.length > 0) {
        await db.outbox.bulkDelete(outboxIds);
      }
      if (metaKeys.length > 0) {
        await db.meta.bulkDelete(metaKeys);
      }
    });
  },

  async setMeta<T>(clientId: number, key: WorkItemsMetaKey, value: T): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row: OfflineMetaRecord = {
      key: buildMetaStorageKey(clientId, key),
      domain: DOMAIN,
      client_id: clientId,
      updated_at: new Date().toISOString(),
      payload: await offlinePayloadCrypto.encryptValue(value),
    };

    await db.meta.put(row);
  },

  async getMeta<T>(clientId: number, key: WorkItemsMetaKey): Promise<T | null> {
    const db = getDb();
    if (!db) {
      return null;
    }

    const row = await db.meta.get(buildMetaStorageKey(clientId, key));
    if (!row) {
      return null;
    }

    return offlinePayloadCrypto.decryptValue<T>(row.payload);
  },

  async deleteMeta(clientId: number, key: WorkItemsMetaKey): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    await db.meta.delete(buildMetaStorageKey(clientId, key));
  },

  async addOutboxEntry<TPayload>(clientId: number, entry: AddOutboxEntryInput<TPayload>): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    const now = new Date().toISOString();
    const row: OfflineOutboxRecord = {
      domain: DOMAIN,
      client_id: clientId,
      operation: entry.operation,
      entity_type: entry.entityType,
      entity_id: toEntityId(entry.entityId),
      status: entry.status || 'pending',
      priority: entry.priority,
      attempts: entry.attempts || 0,
      max_attempts: entry.maxAttempts || 5,
      created_at: toIso(entry.createdAt, now),
      updated_at: toIso(entry.updatedAt, now),
      last_attempt: entry.lastAttempt ? toIso(entry.lastAttempt) : undefined,
      next_retry_at: entry.nextRetryAt ? toIso(entry.nextRetryAt) : undefined,
      error_message: entry.errorMessage,
      payload: await offlinePayloadCrypto.encryptValue(entry.payload),
    };

    return db.outbox.add(row);
  },

  async listOutboxEntries<TPayload>(
    clientId: number,
    status?: WorkItemsOutboxStatus,
  ): Promise<WorkItemsOutboxEntry<TPayload>[]> {
    const db = getDb();
    if (!db) {
      return [];
    }

    const rows = status
      ? await db.outbox.where('[domain+client_id+status]').equals([DOMAIN, clientId, status]).toArray()
      : await db.outbox.where('[domain+client_id]').equals([DOMAIN, clientId]).toArray();

    const entries = await Promise.all(
      rows.map(async (row) => ({
        id: row.id || 0,
        operation: row.operation,
        entity_type: row.entity_type,
        entity_id: row.entity_id,
        payload: await offlinePayloadCrypto.decryptValue<TPayload>(row.payload),
        priority: row.priority,
        status: row.status,
        attempts: row.attempts,
        max_attempts: row.max_attempts,
        last_attempt: toDate(row.last_attempt),
        next_retry_at: toDate(row.next_retry_at),
        error_message: row.error_message,
        created_at: new Date(row.created_at),
        updated_at: new Date(row.updated_at),
      })),
    );

    return entries.sort((left, right) => {
      if (left.priority !== right.priority) {
        return left.priority - right.priority;
      }

      return left.created_at.getTime() - right.created_at.getTime();
    });
  },

  async findOutboxEntry<TPayload>(
    clientId: number,
    entityType: string,
    entityId: string | number,
    options?: { status?: WorkItemsOutboxStatus; operation?: string },
  ): Promise<WorkItemsOutboxEntry<TPayload> | undefined> {
    const db = getDb();
    if (!db) {
      return undefined;
    }

    const rows = await db.outbox
      .where('[domain+client_id+entity_type+entity_id]')
      .equals([DOMAIN, clientId, entityType, toEntityId(entityId)])
      .toArray();

    for (const row of rows) {
      if (options?.status && row.status !== options.status) {
        continue;
      }
      if (options?.operation && row.operation !== options.operation) {
        continue;
      }

      return {
        id: row.id || 0,
        operation: row.operation,
        entity_type: row.entity_type,
        entity_id: row.entity_id,
        payload: await offlinePayloadCrypto.decryptValue<TPayload>(row.payload),
        priority: row.priority,
        status: row.status,
        attempts: row.attempts,
        max_attempts: row.max_attempts,
        last_attempt: toDate(row.last_attempt),
        next_retry_at: toDate(row.next_retry_at),
        error_message: row.error_message,
        created_at: new Date(row.created_at),
        updated_at: new Date(row.updated_at),
      };
    }

    return undefined;
  },

  async updateOutboxEntry<TPayload>(
    clientId: number,
    id: number,
    patch: UpdateOutboxEntryInput<TPayload>,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const existing = await db.outbox.get(id);
    if (!existing || existing.domain !== DOMAIN || existing.client_id !== clientId) {
      return;
    }

    const updated: OfflineOutboxRecord = {
      ...existing,
      operation: patch.operation ?? existing.operation,
      entity_type: patch.entityType ?? existing.entity_type,
      entity_id: patch.entityId !== undefined ? toEntityId(patch.entityId) : existing.entity_id,
      priority: patch.priority ?? existing.priority,
      status: patch.status ?? existing.status,
      attempts: patch.attempts ?? existing.attempts,
      max_attempts: patch.maxAttempts ?? existing.max_attempts,
      created_at: toIso(patch.createdAt, existing.created_at),
      updated_at: toIso(patch.updatedAt),
      last_attempt: patch.lastAttempt === null ? undefined : patch.lastAttempt ? toIso(patch.lastAttempt) : existing.last_attempt,
      next_retry_at: patch.nextRetryAt === null ? undefined : patch.nextRetryAt ? toIso(patch.nextRetryAt) : existing.next_retry_at,
      error_message: patch.errorMessage === null ? undefined : patch.errorMessage ?? existing.error_message,
      payload: patch.payload === undefined ? existing.payload : await offlinePayloadCrypto.encryptValue(patch.payload),
    };

    await db.outbox.put(updated);
  },

  async deleteOutboxEntry(id: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    await db.outbox.delete(id);
  },

  async deleteOutboxWhere<TPayload>(
    clientId: number,
    predicate: (entry: WorkItemsOutboxEntry<TPayload>) => boolean,
  ): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    const entries = await this.listOutboxEntries<TPayload>(clientId);
    const idsToDelete = entries.filter(predicate).map((entry) => entry.id);

    if (idsToDelete.length > 0) {
      await db.outbox.bulkDelete(idsToDelete);
    }

    return idsToDelete.length;
  },
};

export default workItemsStore;
